import { z } from "zod"
import { USER_ROLES } from "@shared/constants/user-constants"

// User schema
export const UserSchema = z.object({
  id: z.string(),
  userName: z.string(),
  fullName: z.string().optional(),
  email: z.string().email(),
  role: z.enum([USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.VIEWER]).optional(),
  avatar: z.string().optional(),
  active: z.boolean().optional(),
  lastActive: z.string().optional(),
  organization: z.string().optional(),
  tenantId: z.string().optional(),
  tenantName: z.string().optional(),
  oriCode: z.string().optional(),
  workstation: z.string().optional(),
  workstationNames: z.array(z.string()).optional().nullable(),
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
  workstationOriCodes: z.array(z.string()).optional().nullable(),
  permissions: z.array(z.number()).optional().nullable(),
  currentPassword: z.string().optional(),
})

export type User = z.infer<typeof UserSchema>

// Registration schema
export const RegisterSchema = z
  .object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    username: z.string().min(3, "Username must be at least 3 characters"),
    email: z.string().email("Please enter a valid email address"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })

export type RegisterFormValues = z.infer<typeof RegisterSchema>

// Login schema
export const LoginSchema = z.object({
  email: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean().optional(),
})

export type LoginFormValues = z.infer<typeof LoginSchema>

// Password reset request schema
export const PasswordResetRequestSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

export type PasswordResetRequestFormValues = z.infer<typeof PasswordResetRequestSchema>

// Password reset schema
export const PasswordResetSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string(),
    token: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })

export type PasswordResetFormValues = z.infer<typeof PasswordResetSchema>

// Auth response schema
export const AuthResponseSchema = z.object({
  id: z.string(),
  userName: z.string(),
  email: z.string().email("Please enter a valid email address"),
  fullName: z.string().optional(),
  token: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  oriCode: z.string().optional(),
  isSuperAdmin: z.boolean(),
  roles: z.array(z.string()),
  lastLoginAt: z.string().optional(),
  requiresEmailConfirmation: z.boolean().optional(),
  lastActive: z.string().nullable().optional(),
  refreshToken: z.string().nullable().optional(),
})

export type AuthResponse = z.infer<typeof AuthResponseSchema>
